import React from 'react';
import { Link as RouterLink } from 'react-router';
import { Box, Typography, useTheme, Avatar, Chip, Link, Grid } from '@mui/material';
import { Person, Headset, CalendarMonth, Apps } from '@mui/icons-material';
import { getDirectionIcon, getDirectionLabel } from '@/components/calls/CallList/callListColumns';
import KeyInfoField from './KeyInfoField';

import type { CallData } from '@/types/calls';
import { useDateFormatter } from '@/hooks';

interface CallKeyInfoHeaderProps {
    call: CallData;
}

/**
 * Displays key information about a call.
 *
 * @param {CallKeyInfoHeaderProps} props
 * @returns {JSX.Element}
 */
const CallKeyInfoHeader: React.FC<CallKeyInfoHeaderProps> = ({ call }) => {
    const theme = useTheme();
    const formatDate = useDateFormatter();

    const agentId = call.agent_id?.toString() || '';
    const agentName = call.agent_name || 'Unknown agent';

    const customerName =
        call.customer_fname && call.customer_lname ? `${call.customer_fname} ${call.customer_lname}` : call.number || 'Unknown customer';

    const direction = call.direction;
    const campaignName = call.campaign_name || '';

    const createIconAvatar = (IconComponent: React.ElementType) => (
        <Avatar
            sx={{
                bgcolor: theme.palette.primary.main,
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                flexShrink: 0,
                borderRadius: 1.5,
            }}
        >
            <IconComponent fontSize="small" sx={{ color: theme.palette.common.white }} />
        </Avatar>
    );

    return (
        <Box
            sx={{
                width: '100%',
                px: 2,
                py: 1.5,
                backgroundColor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`,
            }}
            data-testid="call-key-info-header"
        >
            <Grid container spacing={2}>
                <KeyInfoField label="Date and time" avatar={createIconAvatar(CalendarMonth)}>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', lineHeight: 1.5, mt: 0.25 }}>
                        {formatDate(call.created_at)}
                    </Typography>
                </KeyInfoField>
                <KeyInfoField label="Agent" avatar={createIconAvatar(Headset)}>
                    <Typography variant="body1" sx={{ maxWidth: '200px', wordBreak: 'break-word', lineHeight: 1.5, mt: 0.25 }}>
                        <Link to={`/agents/${agentId}`} component={RouterLink} underline="hover" color="inherit">
                            {agentName}
                        </Link>
                    </Typography>
                </KeyInfoField>
                <KeyInfoField label="Customer" labelMinWidth="70px" avatar={createIconAvatar(Person)}>
                    <Typography
                        variant="body1"
                        sx={{ wordBreak: 'break-word', maxWidth: '200px', color: 'inherit', lineHeight: 1.5, mt: 0.25 }}
                    >
                        {customerName}
                    </Typography>
                </KeyInfoField>
                <KeyInfoField label="Project" avatar={createIconAvatar(Apps)}>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', lineHeight: 2 }}>
                        <Link to={`/projects/${call.campaign_id}`} component={RouterLink} underline="hover" color="inherit">
                            {campaignName || 'N/A'}
                        </Link>
                    </Typography>
                </KeyInfoField>
                <KeyInfoField label="Direction">
                    <Chip
                        label={getDirectionLabel(direction)}
                        color="primary"
                        icon={getDirectionIcon(direction) as React.ReactElement}
                        sx={{ pl: 0.25 }}
                        variant="outlined"
                    />
                </KeyInfoField>
            </Grid>
        </Box>
    );
};

export default CallKeyInfoHeader;
